import { useState } from "react";
import { Link } from "react-router";
import { motion } from "motion/react";
import { ArrowRight, Heart, Target, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { StatsCounter } from "../components/stats-counter";
import { EnhancedProjectCard } from "../components/enhanced-project-card";
import { DonationWidget } from "../components/donation-widget";
import { TransparencySection } from "../components/transparency-section";
import { ImpactPillars } from "../components/impact-pillars";
import { StorytellingSection } from "../components/storytelling-section";
import { TrustBuilders } from "../components/trust-builders";
// import { ProjectMap } from "../components/project-map";
import { projects } from "../data/projects";

export function Home() {
  const [isDonationWidgetOpen, setIsDonationWidgetOpen] = useState(false);
  const featuredProjects = projects.slice(0, 3);

  return (
    <div className="overflow-x-hidden">
      {/* Full-Screen Emotional Hero */}
      <section className="relative h-screen flex items-center justify-center text-center text-white">
        <motion.div
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          className="absolute inset-0 z-0"
        >
          <img
            src="/Banner.jpg"
            alt="Empower communities"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8"
        >
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl mb-8 leading-tight tracking-tight font-bold max-w-4xl mx-auto"
          >
            <span className="whitespace-nowrap">Empower Communities.</span>
            <br />
            <span className="text-secondary">Build Futures.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="text-2xl md:text-3xl mb-12 max-w-3xl mx-auto leading-relaxed"
          >
            We support verified grassroots initiatives with complete transparency that expand education, create jobs, and strengthen rural communities in Himachal Pradesh.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="flex flex-col sm:flex-row gap-6 justify-center"
          >
            <Link to="/get-involved"> 
              <Button
                size="lg"
                className="text-xl px-12 py-8 bg-secondary hover:bg-secondary/90 text-secondary-foreground shadow-2xl"
              >Partner Now</Button>
            </Link>
            <Link to="/projects">
              <Button
                size="lg"
                variant="outline"
                className="text-xl px-12 py-8 bg-white/10 hover:bg-white/20 text-white border-white/30 backdrop-blur-sm"
              >
                See Impact
                <ArrowRight className="ml-2" size={24} />
              </Button>
            </Link>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2"
          >
            <motion.div className="w-1.5 h-1.5 bg-white rounded-full" />
          </motion.div>
        </motion.div>
      </section>

      {/* Impact Statistics - Dynamic Counters */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl mb-4">Impact in Numbers</h2>
            <p className="text-xl text-muted-foreground">
              Real change, measured and verified
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="text-6xl md:text-7xl mb-4 font-bold text-primary">
                <StatsCounter end={2} />
              </div>
              <div className="text-xl">Projects Funded</div>
              <div className="text-sm text-muted-foreground mt-2">
                Across Himachal
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="text-6xl md:text-7xl mb-4 font-bold text-primary">
                <StatsCounter end={15} suffix="+" />
              </div>
              <div className="text-xl">Lives Transformed</div>
              <div className="text-sm text-muted-foreground mt-2">
                Direct beneficiaries
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <div className="text-6xl md:text-7xl mb-4 font-bold text-primary">
                ₹<StatsCounter end={1} suffix="L" />
              </div>
              <div className="text-xl">Funds Distributed</div>
              <div className="text-sm text-muted-foreground mt-2">
                100% to partners
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="text-center"
            >
              <div className="text-6xl md:text-7xl mb-4 font-bold text-primary">
                <StatsCounter end={3} />
              </div>
              <div className="text-xl">Partner & NGOs</div>
              <div className="text-sm text-muted-foreground mt-2">
                Verified organizations
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Impact Pillars - Why It Matters */}
      <ImpactPillars />

      {/* Featured Projects - With Enhanced Cards */}
      <section className="pt-16 pb-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-4xl md:text-6xl mb-6">
              Where Impact 
              <br />
              <span className="text-primary">Is Already Happening</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Together with our partners, volunteers, and contributors, our on-ground initiatives are transforming communities. Join us in creating meaningful and measurable change.
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-8 mb-8 mx-auto">
            {featuredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="flex w-full max-w-[520px] justify-center"
              >
                <EnhancedProjectCard {...project} />
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Link to="/projects">
              <Button size="lg" variant="outline" className="text-lg px-8">
                Explore All Projects
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Transparency Section */}
      <TransparencySection />

      {/* Project Map - Proof System */}
      {/* <ProjectMap projects={projects} /> */}

      {/* Storytelling Section */}
      <StorytellingSection />

      {/* How We Work - Visual Flow */}
      <section className="py-32 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl mb-6">How We Work</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              A simple, transparent process from discovery to impact
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline connector */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-secondary to-accent transform -translate-x-1/2" />

            <div className="space-y-16">
              {/* Step 1 */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative grid md:grid-cols-2 gap-8 items-center"
              >
                <div className="md:text-right">
                  <div className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-bold underline mb-4">
                    Step 1
                  </div>
                  <h3 className="text-3xl mb-4">Discover & Verify</h3>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    We identify grassroots organizations doing exceptional work
                    in sustainable tourism and community development. Each
                    project undergoes rigorous verification.
                  </p>
                </div>
                <div className="relative">
                  <div className="md:pl-16">
                    <Target className="text-primary ml-16 md:ml-0" size={48} />
                  </div>
                </div>
              </motion.div>

              {/* Step 2 */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative grid md:grid-cols-2 gap-8 items-center"
              >
                <div className="md:order-2">
                  <div className="inline-block px-4 py-2 bg-secondary/20 text-secondary-foreground rounded-full text-sm font-bold underline mb-4">
                    Step 2
                  </div>
                  <h3 className="text-3xl mb-4">Connect & Fund</h3>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    Donors choose projects that resonate with them. 100% of your
                    contribution goes directly to the partner organization —
                    zero overhead, total transparency.
                  </p>
                </div>
                <div className="relative md:order-1">
                  <div className="md:pr-16 flex justify-end">
                    <Heart className="text-secondary mr-16" size={48} />
                  </div>
                </div>
              </motion.div>

              {/* Step 3 */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative grid md:grid-cols-2 gap-8 items-center"
              >
                <div className="md:text-right">
                  <div className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-bold underline mb-4">
                    Step 3
                  </div>
                  <h3 className="text-3xl mb-4">Track & Report</h3>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    Receive regular updates with photos, videos, and impact
                    reports. See exactly how your contribution transforms lives
                    and communities.
                  </p>
                </div>
                <div className="relative">
                  <div className="md:pl-16">
                    <CheckCircle2 className="text-accent ml-16 md:ml-0" size={48} />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Builders */}
      <TrustBuilders />

      {/* Final CTA - Emotional & Action-First */}
      <section className="relative overflow-hidden py-32">
        <motion.div
          initial={{ scale: 1.1 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="absolute inset-0"
        >
          <img
            src="/Part%20of%20change.jpg"
            alt="Be Part of the Change"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-primary-foreground"
        >
          <h2 className="text-5xl md:text-6xl mb-8 leading-tight">
            Be Part of the Change
          </h2>
          <p className="text-lg mb-12 opacity-95 leading-relaxed max-w-lg mx-auto">
            Every contribution of time, expertise, or resources helps strengthen real, on-ground initiatives and expand their impact across communities. Make a change with us today.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/get-involved"> {/* Wrap "Partner Now" button with Link */}
              <Button
                size="lg"
                className="text-xl px-12 py-8 bg-secondary hover:bg-secondary/90 text-secondary-foreground shadow-2xl"
              >Partner Now</Button>
            </Link>
            <Link to="/apply">
              <Button
                size="lg"
                variant="outline"
                className="text-xl px-12 py-8 bg-white/10 hover:bg-white/20 border-white/25 text-white backdrop-blur-sm"
              >
                Apply for Funding
              </Button>
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Donate Now button reserved for future use. */}

      {/* Donation Widget */}
      <DonationWidget
        isOpen={isDonationWidgetOpen}
        onClose={() => setIsDonationWidgetOpen(false)}
      />
    </div>
  );
}
